/**
 * GHGBalancePage.tsx — Greenhouse-gas balance for the aeration scheme
 *
 * Quantifies methane production from anoxic sediment, methane oxidation in the
 * aerobic water column, blower CO₂e from electricity, and net 20-year carbon
 * outcome vs do-nothing baseline.
 *
 * References:
 *   DelSontro, T. et al. (2018). Greenhouse gas emissions from lakes and impoundments.
 *   IPCC AR6 WG1 (2021). Climate Change 2021: The Physical Science Basis.
 *   Bastviken et al. (2011). Methane emissions from lakes: dependence of lake characteristics.
 *   UK Defra/BEIS (2024). Conversion factors for greenhouse gas reporting.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Leaf, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import { readOverridesAsNumbers, SIM_DATA_KEY } from '@/pages/SimulationDataPage';
import SimOverrideBanner from '@/components/SimOverrideBanner';

const GWP_CH4 = 27;          // IPCC AR6, 100-year horizon (no carbon-cycle feedback)

const RESERVOIR_AREA_M2 = 34_000;

const KEY = 'rb_ghg_balance_v1';

interface GHGState {
  // Sediment & water
  sodKgM2D: number;            // sediment oxygen demand
  organicMatterKgM2Y: number;  // organic matter input
  anoxicAreaFractionBase: number; // pre-aeration anoxic bed fraction
  anoxicAreaFractionAerated: number;
  // Blower & power
  blowerKW: number;
  blowerHoursPerYearBase: number;
  blowerHoursPerYearAerated: number;
  gridCO2eKgPerKWh: number;    // UK grid intensity 2025
  // Horizon
  horizonYears: number;
}

const DEFAULT_STATE: GHGState = {
  sodKgM2D: 0.6,
  organicMatterKgM2Y: 0.5,
  anoxicAreaFractionBase: 0.45,
  anoxicAreaFractionAerated: 0.08,
  blowerKW: 45,
  blowerHoursPerYearBase: 0,
  blowerHoursPerYearAerated: 4380,   // 50% duty cycle
  gridCO2eKgPerKWh: 0.207,           // DEFRA 2024
  horizonYears: 20,
};

function fmt(n: number, d = 1) { if (!isFinite(n)) return '—'; return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }); }

export default function GHGBalancePage() {
  const navigate = useNavigate();
  const [s, setS] = useState<GHGState>(() => loadPersisted<GHGState>(KEY, DEFAULT_STATE));
  const [, setNonce] = useState(0);
  // Simulation overrides — b9 SOD, b5 LOI organic content
  const [simOver, setSimOver] = useState(() => readOverridesAsNumbers(['b9', 'b5']));

  useEffect(() => { savePersisted(KEY, s); }, [s]);

  useEffect(() => {
    const unsub = onPersistChange(k => {
      if (k === KEYS.DIFFUSER_MODULE) setNonce(n => n + 1);
      if (k === SIM_DATA_KEY) setSimOver(readOverridesAsNumbers(['b9', 'b5']));
    });
    return () => unsub();
  }, []);

  // Apply overrides: SOD directly substitutes, LOI scales the methane emission factor
  // (organic content drives methanogenesis; DelSontro 2018 gives 0.07–0.20 g CH4/m²/d band)
  const sodEff = simOver.activeIds.has('b9') ? simOver.values.b9 : s.sodKgM2D;
  const loiScale = simOver.activeIds.has('b5')
    ? Math.max(0.3, Math.min(2.5, simOver.values.b5 / 15))    // LOI scaled around 15% default
    : 1.0;

  const calc = useMemo(() => {
    // Methane production scales with anoxic area × organic matter input
    // DelSontro (2018) emission factor ~ 0.07–0.20 g CH4 / m² / d for temperate eutrophic.
    // Override: site-measured LOI scales the emission factor (more organic → more methane);
    //          site-measured SOD substitutes the bulk sediment-oxygen-demand input directly.
    // SOD also acts as a proxy for organic loading — higher SOD → more methanogenic potential
    const sodScale = sodEff / s.sodKgM2D;
    const ch4Base_g_m2_d = 0.15 * s.anoxicAreaFractionBase / 0.45 * loiScale * sodScale;
    const ch4Aerated_g_m2_d = 0.15 * s.anoxicAreaFractionAerated / 0.45 * loiScale * sodScale;

    // Diffusive emission ~ 30% escapes; ebullition ~ 50%; the rest oxidised in column
    // With aeration: oxidation in aerated column rises to ~80%
    const fEscapeBase = 0.80;
    const fEscapeAerated = 0.20;

    const ch4Base_kgY = ch4Base_g_m2_d * RESERVOIR_AREA_M2 * 365 / 1000 * fEscapeBase;
    const ch4Aerated_kgY = ch4Aerated_g_m2_d * RESERVOIR_AREA_M2 * 365 / 1000 * fEscapeAerated;

    const ch4Base_tCO2eY = ch4Base_kgY * GWP_CH4 / 1000;
    const ch4Aerated_tCO2eY = ch4Aerated_kgY * GWP_CH4 / 1000;

    // Blower power emissions
    const blowerBase_tCO2eY = s.blowerKW * s.blowerHoursPerYearBase * s.gridCO2eKgPerKWh / 1000;
    const blowerAerated_tCO2eY = s.blowerKW * s.blowerHoursPerYearAerated * s.gridCO2eKgPerKWh / 1000;

    // Avoided emissions (algal bloom CO2 spike, taste & odour treatment with PAC) — small but real
    const avoidedTreatment_tCO2eY = 5;   // conservative

    const totalBase_tCO2eY = ch4Base_tCO2eY + blowerBase_tCO2eY;
    const totalAerated_tCO2eY = ch4Aerated_tCO2eY + blowerAerated_tCO2eY - avoidedTreatment_tCO2eY;
    const netAnnualSavingTCO2e = totalBase_tCO2eY - totalAerated_tCO2eY;
    const horizonSavingTCO2e = netAnnualSavingTCO2e * s.horizonYears;

    // Grid intensity in 2045 forecast to ~30% of today
    const futureGridFactor = 0.30;
    const blowerAeratedFuture_tCO2eY = s.blowerKW * s.blowerHoursPerYearAerated * s.gridCO2eKgPerKWh * futureGridFactor / 1000;
    const futureSavingTCO2e = (ch4Base_tCO2eY + blowerBase_tCO2eY) - (ch4Aerated_tCO2eY + blowerAeratedFuture_tCO2eY - avoidedTreatment_tCO2eY);

    return {
      ch4Base_tCO2eY, ch4Aerated_tCO2eY,
      blowerBase_tCO2eY, blowerAerated_tCO2eY,
      avoidedTreatment_tCO2eY,
      totalBase_tCO2eY, totalAerated_tCO2eY,
      netAnnualSavingTCO2e, horizonSavingTCO2e,
      futureSavingTCO2e,
      blowerAeratedFuture_tCO2eY,
    };
  }, [s, sodEff, loiScale]);

  const updateField = <K extends keyof GHGState>(k: K, v: number) => setS({ ...s, [k]: v });

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Leaf}
        title="GHG Balance — Methane & CO₂e"
        subtitle="20-year tCO₂e balance: sediment methane, oxidation, blower power, avoided treatment. IPCC AR6 GWP."
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      <SimOverrideBanner consumes={[
        { id: 'b9', label: 'SOD',         active: simOver.activeIds.has('b9'), value: `${simOver.values.b9} kg/m²/d` },
        { id: 'b5', label: 'LOI organic', active: simOver.activeIds.has('b5'), value: `${simOver.values.b5}%` },
      ]} />

      {/* Headline */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-red-700 bg-red-900/20 p-4">
          <div className="text-xs text-red-300 uppercase tracking-wider">Baseline (do-nothing)</div>
          <div className="text-2xl font-bold text-red-200 mt-1">{fmt(calc.totalBase_tCO2eY, 1)} tCO₂e/y</div>
          <div className="text-xs text-slate-400 mt-1">CH₄ {fmt(calc.ch4Base_tCO2eY, 1)} + power {fmt(calc.blowerBase_tCO2eY, 1)}</div>
        </div>
        <div className="rounded-xl border border-emerald-700 bg-emerald-900/20 p-4">
          <div className="text-xs text-emerald-300 uppercase tracking-wider">With aeration</div>
          <div className="text-2xl font-bold text-emerald-200 mt-1">{fmt(calc.totalAerated_tCO2eY, 1)} tCO₂e/y</div>
          <div className="text-xs text-slate-400 mt-1">CH₄ {fmt(calc.ch4Aerated_tCO2eY, 1)} + power {fmt(calc.blowerAerated_tCO2eY, 1)} − avoided {fmt(calc.avoidedTreatment_tCO2eY, 1)}</div>
        </div>
        <div className="rounded-xl border-2 border-rb-brand bg-rb-brand/10 p-4">
          <div className="text-xs text-rb-brand uppercase tracking-wider">Net annual saving</div>
          <div className="text-3xl font-bold text-slate-100 mt-1">{fmt(calc.netAnnualSavingTCO2e, 1)} tCO₂e/y</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">{s.horizonYears}-year saving</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(calc.horizonSavingTCO2e, 0)} tCO₂e</div>
        </div>
      </div>

      {/* Future grid */}
      <div className="rounded-xl border border-sky-700 bg-sky-900/20 p-4">
        <div className="text-sm font-semibold text-sky-300 mb-1">Future grid intensity scenario (2045)</div>
        <div className="text-xs text-slate-300">
          UK grid decarbonisation reduces blower-power emissions to ~30 % of today by 2045. Recomputed annual saving under that scenario:
          <span className="font-bold text-sky-200 ml-2">{fmt(calc.futureSavingTCO2e, 1)} tCO₂e/y</span>
        </div>
      </div>

      {/* Inputs */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-3">Inputs</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          {[
            { k: 'sodKgM2D', label: 'Sediment O₂ demand (kg/m²/d)', step: 0.05 },
            { k: 'organicMatterKgM2Y', label: 'Organic input (kg/m²/y)', step: 0.05 },
            { k: 'anoxicAreaFractionBase', label: 'Anoxic bed fraction (baseline)', step: 0.05 },
            { k: 'anoxicAreaFractionAerated', label: 'Anoxic bed fraction (aerated)', step: 0.01 },
            { k: 'blowerKW', label: 'Blower power (kW)', step: 1 },
            { k: 'blowerHoursPerYearBase', label: 'Blower hours/y (baseline)', step: 100 },
            { k: 'blowerHoursPerYearAerated', label: 'Blower hours/y (aerated)', step: 100 },
            { k: 'gridCO2eKgPerKWh', label: 'Grid CO₂e (kg/kWh)', step: 0.01 },
            { k: 'horizonYears', label: 'Horizon (years)', step: 1 },
          ].map(f => (
            <div key={f.k}>
              <label className="text-xs text-slate-400 block mb-1">{f.label}</label>
              <input type="number" step={f.step} value={s[f.k as keyof GHGState]}
                onChange={e => updateField(f.k as keyof GHGState, Number(e.target.value))}
                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
            </div>
          ))}
        </div>
      </div>

      {/* Bar chart comparison */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">tCO₂e/y breakdown</div>
        <svg viewBox="0 0 720 220" className="w-full h-auto bg-slate-950 rounded">
          {(() => {
            const bars = [
              { label: 'Baseline CH₄', value: calc.ch4Base_tCO2eY, colour: '#dc2626' },
              { label: 'Aerated CH₄',  value: calc.ch4Aerated_tCO2eY, colour: '#ef4444' },
              { label: 'Baseline power', value: calc.blowerBase_tCO2eY, colour: '#475569' },
              { label: 'Aerated power',  value: calc.blowerAerated_tCO2eY, colour: '#64748b' },
              { label: 'Avoided treat.',  value: calc.avoidedTreatment_tCO2eY, colour: '#10b981' },
              { label: 'Net saving',      value: calc.netAnnualSavingTCO2e, colour: '#22c55e' },
            ];
            const maxV = Math.max(...bars.map(b => Math.abs(b.value)), 1);
            const padL = 100;
            const plotW = 600;
            return bars.map((b, i) => {
              const yTop = 20 + i * 30;
              const w = (b.value / maxV) * plotW;
              return (
                <g key={i}>
                  <text x={padL - 5} y={yTop + 15} textAnchor="end" fontSize={10} fill="#cbd5e1">{b.label}</text>
                  <rect x={padL} y={yTop + 4} width={Math.max(0, w)} height={18} fill={b.colour} />
                  <text x={padL + Math.max(0, w) + 6} y={yTop + 17} fontSize={10} fill="#e2e8f0">{fmt(b.value, 1)}</text>
                </g>
              );
            });
          })()}
        </svg>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Why this matters
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            UK water utilities are under binding Net Zero commitments. A 34,000 m² eutrophic reservoir
            is a measurable methane source when the bed sits anoxic for 4–5 months a year. Aerating the
            water column shifts methane from the escape pathway (ebullition + diffusion) to in-column
            biological oxidation, converting it to CO₂ at a 27× lower GWP.
          </p>
          <p>
            The headline number for South East Water's stakeholders is the net annual tCO₂e saving and
            the cumulative {s.horizonYears}-year figure. Both are calibratable: when site-measured
            methane flux is available from chamber surveys post-award, the emission factor here gets
            replaced and every downstream figure updates.
          </p>
        </div>
      </div>
    </div>
  );
}
